import { getNestedOrFloatingElementById, getRelationshipLabel } from '@fs/zion-sls'
import uniqBy from 'lodash.uniqby'
import * as Yup from 'yup'
import { translateRelationshipChips } from '../../../../../../utils/translationUtils'

/**
 * Sorts items by their label property in ascending order
 *
 * @param {Object} a - First object with label property
 * @param {Object} b - Second object with label property
 * @returns {number} - Negative if a comes before b, positive if a comes after b, 0 if equal
 */
export function sortByLabel(a, b) {
  if (a.label < b.label) return -1
  if (a.label > b.label) return 1
  return 0
}

/**
 * Gets a new relationship label based on the provided data
 *
 * @param {Object} params - Parameters object
 * @param {Object} params.relationshipField - The relationship field data
 * @param {Object} params.otherPerson - The other person in the relationship
 * @param {Object} params.state - Current state containing images
 * @returns {string|null} - The new relationship label or null if not found
 */
export function getNewLabel({ relationshipField, otherPerson, state }) {
  const slsRelationship = getNestedOrFloatingElementById(state.images, relationshipField.id)
  const order = relationshipField.order
  const typeData = slsRelationship?.typeData
  if (order && typeData) {
    return getRelationshipLabel({ otherPerson, order, typeData })
  }
  return null
}

/**
 * Filters edit highlights based on field type, person IDs, and image ARK
 *
 * @param {string} imageArk - The ARK identifier for the image
 * @param {Array} editHighlights - Array of edit highlights to filter
 * @returns {Array} - Filtered array of edit highlights
 */
export function filterEditHighlights(imageArk, editHighlights) {
  return editHighlights
    ?.filter((i) => ['NAME_SURN', 'NAME_SUF', 'NAME_GN'].includes(i.fieldType))
    .filter((h) => h.personIds.length > 0) // removes floating fields
    .filter((h) => h?.imageId === imageArk)
}

/**
 * Collects highlight IDs for all relationships
 *
 * @param {Object} formValues - Form values containing relationships
 * @param {string} selectedPersonId - ID of the currently selected person
 * @param {Array} editHighlights - Array of edit highlights
 * @param {Array} referenceEditHighlights - Array of reference edit highlights
 * @returns {Object} - Object containing allPeopleHighlights and allPeopleReferenceHighlights
 */
export function collectHighlightIdsForRelationships(
  formValues,
  selectedPersonId,
  editHighlights,
  referenceEditHighlights
) {
  if (!formValues.relationships) return { allPeopleHighlights: [], allPeopleReferenceHighlights: [] }

  const allOtherPeopleIds = formValues.relationships.reduce((acc, rel) => {
    if (rel?.value?.key) {
      acc.push(rel.value.key)
    }
    return acc
  }, [])

  const allIds = allOtherPeopleIds.concat(selectedPersonId)

  const allPeopleHighlights = uniqBy(
    allIds?.flatMap((id) => editHighlights.filter((highlight) => highlight.personIds.includes(id))),
    'id'
  )

  const allPeopleReferenceHighlights = uniqBy(
    allIds?.flatMap((id) => referenceEditHighlights.filter((highlight) => highlight.personIds.includes(id))),
    'id'
  )

  return { allPeopleHighlights, allPeopleReferenceHighlights }
}

/**
 * Finds relationships that have been removed
 *
 * @param {Array} relationships - Array of all relationships
 * @param {Array} selectedChips - Array of selected relationship chips
 * @returns {Array} - Array of relationships that have been removed
 */
export function findRemovedRelationships(relationships, selectedChips) {
  const selectedIds = selectedChips.map(({ id }) => id)
  return relationships.filter((field) => !selectedIds.includes(field.id))
}

/**
 * Finds newly added relationship chips
 *
 * @param {Array} chips - Array of relationship chips
 * @returns {Array} - Array of newly added chips (those without an ID)
 */
export function findNewlyAddedChips(chips) {
  return chips.filter((chip) => !chip.id)
}

/**
 * Creates a relationship object from a chip
 *
 * @param {Object} chip - The relationship chip data
 * @param {string} uuid - Unique identifier to assign to the relationship
 * @returns {Object} - New relationship object with data from the chip
 */
export function createRelationshipFromChip(chip, uuid) {
  return {
    label: chip.label,
    id: uuid,
    relType: chip.reltype,
    order: chip.order,
    value: {
      key: '',
      primaryText: '',
    },
    added: true,
  }
}

/**
 * Returns an array of common relationship types
 *
 * @returns {Array<string>} - Array of common relationship type codes
 */
export function getCommonRelationships() {
  return ['PARENT_CHILD', 'DIVORCED_COUPLE', 'DOMESTIC_PARTNERSHIP', 'FIANCE', 'COUPLE', 'SIBLING']
}

/**
 * Returns an array of family relationship types
 *
 * @returns {Array<string>} - Array of family relationship type codes
 */
export function getFamilyRelationships() {
  return [
    'ADOPTIVE_PARENT',
    'AUNT_OR_UNCLE',
    'PARENT_CHILD_IN_LAW',
    'COUSIN',
    'FOSTER_PARENT',
    'GOD_PARENT',
    'GRAND_PARENT',
    'GUARDIAN_PARENT',
    'RELATIVE',
    'SIBLING_IN_LAW',
    'STEP_PARENT',
    'STEP_SIBLING',
    'SURROGATE_PARENT',
  ]
}

/**
 * Gets relationship chip data organized into categories
 *
 * @param {Function} t - Translation function
 * @param {Object} state - Current state containing relationship types
 * @returns {Array} - Array of relationship chip data grouped by category
 */
export function getRelationshipChipData(t, state) {
  const commonRelationships = getCommonRelationships()
  const familyRelationships = getFamilyRelationships()
  const relationshipChipsFromTypes = translateRelationshipChips(state.types, t).map((chip) => {
    const reltype = chip.relType // this property makes it to the DOM as an attribue on a DOM element, the DOM does not like camelCase
    delete chip.relType
    return { ...chip, reltype }
  })
  return [
    {
      title: t('edit-relationships.most-common-relationships', 'Most Common Relationships'),
      key: 'common-relationships',
      data: relationshipChipsFromTypes?.filter((chip) => commonRelationships.includes(chip.reltype)).sort(sortByLabel),
    },
    {
      title: t('edit-relationships.family-relationships', 'Family Relationships'),
      key: 'family-relationships',
      data: relationshipChipsFromTypes?.filter((chip) => familyRelationships.includes(chip.reltype)).sort(sortByLabel),
    },
    {
      title: t('edit-relationships.other-relationships', 'Other Relationships'),
      key: 'other-relationships',
      data: relationshipChipsFromTypes
        ?.filter((chip) => !commonRelationships.includes(chip.reltype) && !familyRelationships.includes(chip.reltype))
        .sort(sortByLabel),
    },
  ]
}

/**
 * Creates a Yup validation schema for relationship fields
 *
 * @param {Function} t - Translation function
 * @returns {Array} - Array of field ID and validation schema pairs
 */
export function createRelationshipValidationShape(t) {
  return {
    relationships: Yup.array().of(
      Yup.object().shape({
        value: Yup.mixed()
          .nullable()
          .test(
            'is-invalid-relationship',
            t(
              'edit-relationships.autosuggest.invalid-relationship-warning',
              "Name can't be both a child and a spouse. Remove field or change the name."
            ),
            (value, context) => {
              let isValid = true
              const currentType = context.parent.relType
              const conflictingPairs = {
                PARENT_CHILD: 'COUPLE',
                COUPLE: 'PARENT_CHILD',
              }

              // Skip validation if we don't have a conflicting type to check against
              if (!conflictingPairs[currentType]) return true

              const relationshipList = context.from.find((from) => Object.hasOwn(from.value, 'relationships')).value
                .relationships

              // Check for conflicts with other relationships
              relationshipList.forEach((rel) => {
                if (
                  rel.id !== context.parent.id &&
                  rel.value?.key === value?.key &&
                  rel.relType === conflictingPairs[currentType]
                ) {
                  isValid = false
                }
              })
              return isValid
            }
          )
          .test(
            'is-unique-type-value',
            t('edit-relationships.autosuggest.unique-type-value-warning', 'This relation already exists.'),
            (value, context) => {
              let isUnique = true
              const currentType = context.parent.relType
              const relationshipList = context.from.find((from) => Object.hasOwn(from.value, 'relationships')).value
                .relationships

              // Check for conflicts with other relationships
              relationshipList.forEach((rel) => {
                if (rel.id !== context.parent.id && rel.value?.key === value?.key && rel.relType === currentType) {
                  isUnique = false
                }
              })
              return isUnique
            }
          )
          .test('is-empty', t('FormValidation.notEmpty', 'Field cannot be empty.'), (field) => {
            return field?.primaryText
          }),
      })
    ),
  }
}

/**
 * Get form defaults values for editing relationships
 * @param {Object} selectedPerson
 * @param {Array} relationshipOptions
 * @param {Array} relationshipFields
 * @returns {{}}
 */
export function getEditRelationshipDefaults(selectedPerson, relationshipOptions, relationshipFields) {
  const relationshipFieldDefaults = relationshipFields.map((relField) => {
    const strParts = relField.relKey.split('_')
    return {
      id: relField.id,
      label: relField.label,
      value: relationshipOptions.find((rO) => rO.key === relField.otherPersonId) || {
        key: '',
        primaryText: '',
      },
      relType: strParts.slice(0, -1).join('_'), // e.g. 'PARENT_CHILD'
      order: strParts.at(-1),
    }
  })

  return { relationships: relationshipFieldDefaults }
}
